// MISSING_CONTACT_ROUTE — the ONE task that actually IS "no contact on file,"
// so it can be the thing Exceptions shows instead of every task type that
// happens to belong to a contact-less job. Other workflows resolve their
// recipient via proxyFallbackSolicitors.ts's resolveOutreachContact() and,
// when it finds nobody, simply draft no external email. Contests are left
// out: ICT-3 gives them their own fallback solicitor list.
import type { ProxyJob } from '../mockProxyJobs';
import { PROXY_SEED_TODAY } from '../mockProxyJobs';
import { resolveOutreachContact } from '../proxyFallbackSolicitors';
import { resolveProxyJob, hasOnlySkippedMaterial } from '../../utils/proxyQueue';
import { opsNoteHit } from '../../utils/followUpReportPull';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition, ProxyPanelContext, ProxyReviewPanelSchema,
} from '../proxyWorkflowTypes';

// Scan-number stub jobs (UNMATCHED_MATERIAL_ROUTE's "job" — no real job
// number exists at all) already carry their own exception task; there's no
// job-level "solicitor" concept for those, so don't double-flag them here.
function isUnmatchedMaterialStub(job: ProxyJob): boolean {
  return job.jobId.startsWith('S-P');
}

export function missingContactResolved(job: ProxyJob): boolean {
  return job.followUpHistory.some(r => r.type === 'missing_contact_verify');
}

function buildTask(job: ProxyJob): ProxyTask {
  return {
    id: `missing-contact-${job.jobId}`,
    workflowType: 'MISSING_CONTACT_ROUTE',
    jobId: job.jobId,
    title: `${job.issuer} - No contact on file`,
    subtitle: `Job ${job.jobId}`,
    reason: 'No solicitor on the Solicitor Contact List and no prior follow-up on this job. Ace won\'t draft external outreach until a contact is found.',
    source: 'job_signal',
    sourceLabel: 'job record',
    sourceDetail: {
      rows: [
        { label: 'Solicitor on file', value: 'None' },
        { label: 'Prior follow-ups', value: 'None' },
      ],
    },
    createdAt: PROXY_SEED_TODAY,
    confidence: 95,
  };
}

// Only jobs where the procedure actually calls for writing to someone today:
// a chase, a POD request or an email-only notice. Received, aborted,
// e-consent-only, ops-note-excluded and VIF/SMRF-only jobs need nobody, and
// a Red job at 18-11 days searches EDGAR/SEDAR before anyone is contacted.
const NEEDS_CONTACT = new Set(['FOLLOW_UP', 'POD', 'EMAIL_ONLY_CANCEL_FULLSETS']);
function needsOutreach(job: ProxyJob): boolean {
  const resolved = resolveProxyJob(job, null);
  return job.status !== 'ABORTED' && resolved.effectiveStatus === 'ACTIVE' && !opsNoteHit(job)
    && !hasOnlySkippedMaterial(job) && NEEDS_CONTACT.has(resolved.decision.action);
}

function buildTasks({ jobs }: ProxyWorkflowContext): ProxyTask[] {
  return jobs
    .filter(job => !job.contest && !isUnmatchedMaterialStub(job) && !resolveOutreachContact(job) && !missingContactResolved(job) && needsOutreach(job))
    .map(buildTask);
}

function defaultEdits(): Record<string, any> {
  return {}; // nothing here is human-editable
}

function buildPanel({ job }: ProxyPanelContext): ProxyReviewPanelSchema {
  const subject = `Contact tracing needed - ${job.issuer} (Job ${job.jobId})`;
  const body = `We have no solicitor or issuer contact for this job. There's no Broker Search Card solicitor on the Solicitor Contact List and no prior follow-ups. Per ICT-1, please check last year's job for who handled it, or run Contact Tracing, and confirm the contact so outreach can go out.`;

  return {
    extractedFields: [
      { key: 'issuer', label: 'Issuer', display: job.issuer },
      { key: 'jobId', label: 'Job #', display: job.jobId },
      { key: 'cusip', label: 'CUSIP', display: job.cusip },
    ],
    calculation: {
      title: 'Contact status',
      ruleText: 'Ace resolves outreach from the Solicitor Contact List, then from the last party followed up on the job (ICT-1). Neither exists for this job.',
      rows: [],
      totalRow: { label: 'Status', value: 'Missing contact, so no external email will be drafted', tone: 'amber' },
    },
    drafts: [
      { key: 'verify', icon: '📧', label: 'Contact tracing request → ICT Manager', to: 'ict-manager@betanxt.com', subject, body },
    ],
    approveSummary: [
      'Send the contact-tracing request',
      'Log a follow-up on this job',
      'Move this task to Done.',
    ],
    approveConfirmation: `Sent a contact-tracing request for <strong>${job.issuer}</strong>.`,
    closesTicket: true,
    outboundActions: [
      { key: 'verify', followUpType: 'missing_contact_verify', recipientEmail: 'ict-manager@betanxt.com', subject, bodyPreview: body },
    ],
  };
}

export const MISSING_CONTACT_ROUTE_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'MISSING_CONTACT_ROUTE',
  label: 'Missing Contact',
  noun: 'a contact-verification request',
  trigger: 'Ace can\'t resolve an outreach contact for a non-contest job: no solicitor on the Solicitor Contact List and no prior follow-up.',
  source: 'Job signal: ProxyJob.solicitor + the Solicitor Contact List + followUpHistory.',
  steps: ['Source & extraction', 'Contact status', 'Verify & approve'],
  buildTasks,
  defaultEdits,
  buildPanel,
};
